import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import {
  resolveAccountDefaultTranslationLanguage,
  resolveOutgoingLanguage,
  useRealtimeStore
} from "../../lib/store.js";
import { selectTranslateLanguages } from "../../lib/translate-languages.js";

// Переключатель языков перевода в шапке чата. В редизайне 2026-09 ряд кнопок
// над лентой (где жили селекторы «читать»/«писать») скрыт целиком, и языки
// стало не переключить: у аккаунта с двумя вариантами промпта («обращаясь к
// девушке» / «к мужчине») всегда работал только дефолтный. Кнопка показывает
// текущий язык «писать» и раскрывает оба селектора.
export const TranslationLanguagePicker = () => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selectedChatId = useRealtimeStore((s) => s.selectedChatId);
  const languages = useRealtimeStore(useShallow((s) => selectTranslateLanguages(s)));
  const readingLang = useRealtimeStore((s) =>
    s.selectedChatId
      ? (s.translationLanguageByChat[s.selectedChatId] ??
        resolveAccountDefaultTranslationLanguage(s))
      : resolveAccountDefaultTranslationLanguage(s)
  );
  const outgoingLang = useRealtimeStore((s) => resolveOutgoingLanguage(s, s.selectedChatId));
  const setChatTranslationLanguage = useRealtimeStore((s) => s.setChatTranslationLanguage);
  const setOutgoingLanguage = useRealtimeStore((s) => s.setOutgoingLanguage);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Закрываем при смене чата — попап привязан к конкретному чату.
  useEffect(() => {
    setOpen(false);
  }, [selectedChatId]);

  if (!selectedChatId) return null;

  const labelFor = (code: string): string =>
    languages.find((language) => language.code === code)?.label ?? code.toUpperCase();
  const outgoingLabel = outgoingLang.length > 0 ? labelFor(outgoingLang) : "—";

  return (
    <div className="lang-picker" ref={rootRef}>
      <button
        type="button"
        className={open ? "hbtn lang-picker__btn hbtn--active" : "hbtn lang-picker__btn"}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Языки перевода"
        title={`Языки перевода: читать — ${labelFor(readingLang)}, писать клиенту — ${outgoingLabel}`}
      >
        <i className="ic ic-language" aria-hidden="true" />
        <span className="lang-picker__code">{outgoingLabel}</span>
      </button>
      {open ? (
        <div className="lang-picker__pop" role="dialog" aria-label="Языки перевода">
          <label className="lang-picker__row">
            <span className="lang-picker__label">
              <strong>Читать</strong>
              <em>на какой язык переводить входящие</em>
            </span>
            <select
              value={readingLang}
              onChange={(event) => setChatTranslationLanguage(selectedChatId, event.target.value)}
              aria-label="Язык, на который переводятся входящие"
            >
              {languages.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
              {languages.every((option) => option.code !== readingLang) ? (
                <option value={readingLang}>{readingLang.toUpperCase()}</option>
              ) : null}
            </select>
          </label>
          <label className="lang-picker__row">
            <span className="lang-picker__label">
              <strong>Писать</strong>
              <em>язык клиента для перевода черновика</em>
            </span>
            <select
              value={outgoingLang}
              onChange={(event) => setOutgoingLanguage(selectedChatId, event.target.value)}
              aria-label="Язык клиента: на него переводится ваш черновик"
            >
              {outgoingLang.length === 0 ? <option value="">— выбрать —</option> : null}
              {languages.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
              {outgoingLang.length > 0 &&
              languages.every((option) => option.code !== outgoingLang) ? (
                <option value={outgoingLang}>{outgoingLang.toUpperCase()}</option>
              ) : null}
            </select>
          </label>
          <p className="lang-picker__hint">
            Выбор запоминается для этого чата и становится дефолтом аккаунта.
          </p>
        </div>
      ) : null}
    </div>
  );
};
